/**
 * Pure list-cache helpers for the proposal WebSocket cache mutation engine.
 *
 * Extracted from `applyProposalEvent.ts` — these have no QueryClient
 * dependency and are shared by both the create and update merge paths.
 */

import type { QueryKey } from '@tanstack/react-query';

import type {
  FilterTagCounts,
  ProposalListFilters,
  ProposalListItem,
} from '../core/types';

/**
 * The filters object lives at the tail of the list query key:
 *   ['proposals', orgId, 'list', filters?]
 * Extract it (or `undefined` if no filters were passed when keying).
 */
export function extractFilters(
  queryKey: QueryKey
): ProposalListFilters | undefined {
  const tail = queryKey[queryKey.length - 1];
  if (tail && typeof tail === 'object' && !Array.isArray(tail)) {
    return tail as ProposalListFilters;
  }
  return undefined;
}

/**
 * Whether a proposal would satisfy the constraints of a given filter set.
 *
 * - `assigned_to_me` is enforced server-side (the WS push only fans out to
 *   assignees), so we don't re-check it on the client.
 * - `page`, `page_size`, and `include_recipient_data` don't affect membership.
 */
export function proposalMatchesFilters(
  proposal: ProposalListItem,
  filters: ProposalListFilters | undefined
): boolean {
  if (!filters) {
    return true;
  }
  if (filters.resolution && proposal.resolution !== filters.resolution) {
    return false;
  }
  if (
    filters.filter_tags__contains &&
    !proposal.filter_tags.includes(filters.filter_tags__contains)
  ) {
    return false;
  }
  if (
    filters.source_type_name &&
    proposal.source_type_name !== filters.source_type_name
  ) {
    return false;
  }
  return true;
}

/**
 * Merge an incoming proposal into an existing entry, preserving fields
 * the WS payload doesn't carry.
 *
 * The broadcast serializer runs in fast mode (`include_recipient_data=False`),
 * so WS payloads always have `null` for `primary_recipient_email`,
 * `associated_contact`, and `associated_company`. Caches keyed with
 * `include_recipient_data=true` may already hold richer values (populated
 * by detail/list fetches that opted into recipient lookups). Replacing
 * wholesale would erase that data; instead, fall back to the existing
 * entry's values when the WS payload doesn't supply them.
 */
export function mergeProposalEntry(
  existing: ProposalListItem,
  incoming: ProposalListItem
): ProposalListItem {
  return {
    ...incoming,
    primary_recipient_email:
      incoming.primary_recipient_email ?? existing.primary_recipient_email,
    associated_contact:
      incoming.associated_contact ?? existing.associated_contact,
    associated_company:
      incoming.associated_company ?? existing.associated_company,
    // WS broadcast serializer always sends a freshly-created (unread) proposal,
    // so incoming.read_at is null. Preserve the existing entry's read_at so
    // a replace event for an already-read proposal does not reset it back to
    // unread.
    read_at: incoming.read_at ?? existing.read_at,
  };
}

/**
 * Bump `filter_tag_counts` for each tag on a newly inserted proposal so
 * tag/onboarding counters stay in sync with the inserted row until the
 * next refetch. Replacements don't change membership and don't bump.
 */
export function bumpFilterTagCounts(
  current: FilterTagCounts | undefined,
  proposal: ProposalListItem
): FilterTagCounts {
  const next: FilterTagCounts = { ...(current ?? {}) };
  for (const tag of proposal.filter_tags) {
    next[tag] = (next[tag] ?? 0) + 1;
  }
  return next;
}

/**
 * Decrement `filter_tag_counts` for each tag on a proposal that's being
 * removed from a cache (reject-side movement), so tag/onboarding counters
 * stay in sync until the next refetch. Mirrors `bumpFilterTagCounts` but
 * subtracts, flooring at 0 and dropping the key rather than going negative.
 */
export function unbumpFilterTagCounts(
  current: FilterTagCounts | undefined,
  proposal: ProposalListItem
): FilterTagCounts {
  const decremented: FilterTagCounts = { ...(current ?? {}) };
  for (const tag of proposal.filter_tags) {
    decremented[tag] = (decremented[tag] ?? 0) - 1;
  }
  return Object.fromEntries(
    Object.entries(decremented).filter(([, count]) => count > 0)
  );
}
